import { useCallback, useMemo, useRef, useState } from 'react';
import type { GraphQLError } from 'graphql';
import { plural } from '@frameio/i18n';
import { useLoadingToast } from '@frameio/shared/resources/shared/hooks';
import { useTrack } from '@frameio/shared/segment/hooks';
import { startEvent } from '@frameio/telemetry';
import { CoreAssetsEventNames } from '@frameio/telemetry/events/analytics/core-assets';
import { BULK_OPERATION_THRESHOLD } from '../../../bulk-operations/constants';
import { DeleteWarningDialog } from '../../components';
import { useRemoveAssetsFromClipboard } from '../../contexts/AssetClipboardContext';
import { checkCanPerformBulkAction } from '../../utils';
import { useBulkActionLimitToast } from '../useBulkActionLimitToast';
import TEXT from './generated/TEXT/useDeleteAssets';
import { useBulkDeleteAssets } from './useBulkDeleteAssets';
import { useSyncDeleteAssets } from './useSyncDeleteAssets';

export type DeleteAssetsFuncArgs = {
  assetIds: string[] | string;
  force?: boolean;
  onSuccess?: (parentId: string, assetIds: string[]) => void;
  parentId: string;
  projectId: string;
  redirectTo?: string;
};

export type DeleteAssetsReturn = {
  deleteAssets: (args: DeleteAssetsFuncArgs) => void;
  deleteWarningDialog: React.ReactNode;
};

type WarningArgs = {
  ids: string[];
  onSuccess?: (parentId: string, assetIds: string[]) => void;
  parentId: string;
  projectId: string;
  redirectTo?: string;
};

/**
 * The useDeleteAssets hook can be used to delete a set of assets. It renders a
 * warning dialog for the user to confirm then performs any necessary cache cleanup
 * if the delete succeeds.
 *
 * @param opts.onSuccess an optional callback fired if the delete succeeds
 * @returns value.deleteAssets a function to call when ready to delete the assets
 * @returns value.deleteWarningDialog an AlertDialog that needs to be rendered for
 *                                    confirming that the user does want to proceed
 *                                    with the delete
 */
export function useDeleteAssets(): DeleteAssetsReturn {
  const [showWarning, setShowWarning] = useState(false);
  const warningArgs = useRef<WarningArgs | null>(null);
  const dismissRef = useRef(null);
  const { addToast, updateAndHideToast } = useLoadingToast();
  const track = useTrack();
  const addBulkActionLimitToast = useBulkActionLimitToast();
  const deleteAssets = useSyncDeleteAssets();
  const bulkDeleteAssets = useBulkDeleteAssets();
  const removeAssetsFromClipboard = useRemoveAssetsFromClipboard();

  const { errorMessage, deleteLoading, deleteSuccess } = TEXT;

  /**
   * @param assetIds the id or array of ids for the asset(s) to delete
   * @param parentId the parentId of the assets to delete
   * @param projectId the id of the project the asset is in
   * @param force force the delete, without showing the warning
   * @param redirectTo optional URL to redirect to on success
   */
  const deleteAssetsCallback = useCallback(
    async ({
      assetIds,
      force = false,
      onSuccess,
      parentId,
      projectId,
      redirectTo,
    }: DeleteAssetsFuncArgs) => {
      const ids = typeof assetIds === 'string' ? [assetIds] : assetIds;
      const isBulkAssetOperation = ids.length >= BULK_OPERATION_THRESHOLD;
      let toastId: string;

      if (!force) {
        warningArgs.current = {
          ids,
          onSuccess,
          parentId,
          projectId,
          redirectTo,
        };
        setShowWarning(true);
        return;
      }

      track(
        'assets-deleted',
        {
          assets_count: Array.isArray(assetIds) ? assetIds.length : 1,
          is_bulk_operation: isBulkAssetOperation,
        },
        { logToDatadog: true }
      );

      if (
        !checkCanPerformBulkAction({
          assetCount: assetIds.length,
          action: 'delete',
        })
      ) {
        addBulkActionLimitToast('delete');
        return;
      }

      if (isBulkAssetOperation) {
        await bulkDeleteAssets({ assetIds: ids, projectId });
        return;
      }

      const deleteEvent = startEvent(CoreAssetsEventNames.DeleteAssets, {
        asset_count: ids.length,
        asset_ids: ids,
        parent_id: parentId,
        project_id: projectId,
      });

      const handleError = (errors: string[] | readonly GraphQLError[]) => {
        const failedCount = errors?.length || ids.length;

        deleteEvent.failed({ error: errors });
        updateAndHideToast(toastId, {
          title: errorMessage(failedCount) as string,
          variant: 'error',
          ...TOAST_OPTIONS,
        });
      };

      const handleSuccess = (
        deletedAssetsParentId: string,
        deletedAssetIds: string[]
      ) => {
        removeAssetsFromClipboard(deletedAssetIds);
        deleteEvent.completed();
        onSuccess?.(deletedAssetsParentId, deletedAssetIds);
        updateAndHideToast(
          toastId,
          {
            title: deleteSuccess(ids.length) as string,
            variant: 'success',
            ...TOAST_OPTIONS,
          },
          { page: 'trash', projectId }
        );
      };

      toastId = addToast(deleteLoading(ids.length) as string);
      await deleteAssets({
        assetIds: ids,
        parentId,
        projectId,
        onSuccess: handleSuccess,
        onError: handleError,
        redirectTo,
      });
    },
    [
      addBulkActionLimitToast,
      addToast,
      bulkDeleteAssets,
      deleteAssets,
      deleteLoading,
      deleteSuccess,
      errorMessage,
      removeAssetsFromClipboard,
      track,
      updateAndHideToast,
    ]
  );

  const handleDelete = useCallback(() => {
    if (!warningArgs.current) return;

    const { ids, onSuccess, parentId, projectId, redirectTo } =
      warningArgs.current;

    deleteAssetsCallback({
      assetIds: ids,
      force: true,
      onSuccess,
      parentId,
      projectId,
      redirectTo,
    });
    setShowWarning(false);
  }, [deleteAssetsCallback]);

  const deleteWarningDialog = useMemo(
    () => (
      <DeleteWarningDialog
        dismissRef={dismissRef}
        isShowing={showWarning}
        itemCount={warningArgs.current?.ids.length ?? 0}
        onCancelClick={() => {
          setShowWarning(false);
        }}
        onDeleteClick={handleDelete}
        onExited={() => {
          warningArgs.current = null;
        }}
        onRequestClose={() => {
          setShowWarning(false);
        }}
      />
    ),
    [handleDelete, showWarning]
  );

  return {
    deleteAssets: deleteAssetsCallback,
    deleteWarningDialog,
  };
}

const TOAST_OPTIONS = { duration: 4000 };

TEXT.DEFINE({
  errorMessage: (count = 0) =>
    plural(count, {
      one: `Couldn't delete asset`,
      other: `Couldn't delete ${count} assets`,
    }),
  deleteLoading: (count = 0) =>
    plural(count, {
      one: 'Deleting Asset…',
      other: `Deleting ${count} Assets…`,
    }),
  deleteSuccess: (count = 0) =>
    plural(count, {
      one: 'Asset deleted',
      other: `${count} Assets deleted`,
    }),
});
